import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useMySubscription } from './useMySubscription';

/**
 * "Should this session be treated as a Mowazaf Connect (tier `Free`) tenant right now?"
 *
 * Extracted from MasterLayout (Phase 5 route-level Free-tier lock) so the layout's LockedShell
 * decision and the navigation filter (Connect-hidden modules, owner request 2026-10-05) can never
 * disagree. Semantics are unchanged from the inline version:
 *
 *   - Only ever true on a RESOLVED `/api/Subscription/me` payload. Before it resolves,
 *     `coerceTier(undefined)` reads "Free", and on error the app fails OPEN (see the
 *     `subscriptionResolved` note in MasterLayout: an errored /me once wrapped a PAID tenant in
 *     the Free-tier brochure).
 *   - Operators are never treated as Free, EXCEPT through the `?previewTier=free` dogfooding
 *     hook, which is honoured only when the live session really is an Operator, so a regular user
 *     cannot smuggle the lock in via URL.
 *
 * Must be called inside the router (uses `useLocation`).
 */
export function useTreatAsFree(): boolean {
  const location = useLocation();
  const { isFree, isOperator, data } = useMySubscription();
  const previewFree = useMemo(() => {
    if (!isOperator) return false;
    try {
      const params = new URLSearchParams(location.search);
      return (params.get('previewTier') || '').toLowerCase() === 'free';
    } catch {
      return false;
    }
  }, [isOperator, location.search]);
  const subscriptionResolved = data !== null;
  return subscriptionResolved && ((isFree && !isOperator) || previewFree);
}

export default useTreatAsFree;
